import * as XLSX from "xlsx";

function normalizeExportRows(payload) {
  if (typeof payload === "string") {
    const workbook = XLSX.read(payload, { type: "string" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    return XLSX.utils.sheet_to_json(worksheet, { defval: "" });
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload && payload.data)) {
    return payload.data;
  }

  return [];
}

function pickColumnKey(sampleRow, aliases) {
  if (!sampleRow) return "";

  const keys = Object.keys(sampleRow);
  const normalizedMap = keys.reduce((acc, key) => {
    acc[key.toLowerCase().replace(/[^a-z0-9]/g, "")] = key;
    return acc;
  }, {});

  for (const alias of aliases) {
    const direct = keys.find((key) => key === alias);
    if (direct) return direct;

    const normalizedAlias = alias.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (normalizedMap[normalizedAlias]) {
      return normalizedMap[normalizedAlias];
    }
  }

  return "";
}

function sanitizeSheetName(name, usedNames) {
  const base =
    String(name || "Sheet")
      .replace(/[[\]\\/?*:]/g, "-")
      .trim() || "Sheet";

  let candidate = base.slice(0, 31);
  let counter = 1;

  while (usedNames.has(candidate)) {
    const suffix = ` (${counter})`;
    candidate = `${base.slice(0, Math.max(0, 31 - suffix.length))}${suffix}`;
    counter += 1;
  }

  usedNames.add(candidate);
  return candidate;
}

// API rows (not the server CSV, which already has these columns): the enrolment officer's name instead of
// the id, and the enrollee's public verify page (what their QR code opens) as the last column.
const VERIFY_BASE_URL = "https://app.claimify.ng/verify-user";

function withEnrolledByAndQr(row) {
  if (!row || typeof row !== "object" || row["Enrollee ID"] !== undefined) return row;
  const out = { ...row };
  const officer = row.userenrolledby || (row.user && row.user.userenrolledby);
  if (officer) out["Enrolled By"] = officer.full_name || [officer.lastname, officer.firstname].filter(Boolean).join(" ");
  if (row.id != null && row.id !== "") {
    const dependant = row.type === "Dependent" || (row.user_id != null && row.account_type !== "Principal");
    out["QR Code Link"] = `${VERIFY_BASE_URL}/${dependant ? "dependant" : "client"}/${row.id}`;
  }
  return out;
}

function buildExportWorkbook(rows, groupBy) {
  const workbook = XLSX.utils.book_new();
  const usedNames = new Set();
  const headers = rows.length ? Object.keys(rows[0]) : [];

  if (!rows.length) {
    const worksheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enrollees");
    return workbook;
  }

  if (groupBy === "provider") {
    const providerKey = pickColumnKey(rows[0], [
      "Provider",
      "provider",
      "provider_name",
      "Health Provider",
    ]);

    const sheetMap = {};

    rows.forEach((row) => {
      const groupName =
        String(row[providerKey] || "Unknown Provider").trim() ||
        "Unknown Provider";

      if (!sheetMap[groupName]) {
        const sheetName = sanitizeSheetName(groupName, usedNames);
        const worksheet = headers.length
          ? XLSX.utils.aoa_to_sheet([headers])
          : XLSX.utils.aoa_to_sheet([]);

        sheetMap[groupName] = {
          worksheet,
        };

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      }

      XLSX.utils.sheet_add_json(sheetMap[groupName].worksheet, [row], {
        header: headers,
        skipHeader: true,
        origin: -1,
      });
    });

    return workbook;
  }

  if (groupBy === "hmo") {
    const hmoKey = pickColumnKey(rows[0], ["HMO", "hmo", "org_name"]);
    const establishmentKey = pickColumnKey(rows[0], [
      "Establishment",
      "place_of_work",
      "Place of Work",
      "MDA",
    ]);

    const sheetMap = {};

    rows.forEach((row) => {
      const hmo = String(row[hmoKey] || "Unknown HMO").trim() || "Unknown HMO";
      const establishment =
        String(row[establishmentKey] || "Unknown Establishment").trim() ||
        "Unknown Establishment";
      const groupName = `${hmo} - ${establishment}`;

      if (!sheetMap[groupName]) {
        const sheetName = sanitizeSheetName(groupName, usedNames);
        const worksheet = headers.length
          ? XLSX.utils.aoa_to_sheet([headers])
          : XLSX.utils.aoa_to_sheet([]);

        sheetMap[groupName] = {
          worksheet,
        };

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      }

      XLSX.utils.sheet_add_json(sheetMap[groupName].worksheet, [row], {
        header: headers,
        skipHeader: true,
        origin: -1,
      });
    });

    return workbook;
  }

  const worksheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Enrollees");
  return workbook;
}

export function downloadGroupedXlsx(payload, groupBy, fileName) {
  const exportRows = normalizeExportRows(payload).map(withEnrolledByAndQr);
  const workbook = buildExportWorkbook(exportRows, groupBy);
  XLSX.writeFile(workbook, fileName);
}
